import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ShieldCheck, 
  MapPin, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  CheckCircle, 
  ChevronRight, 
  Loader2, 
  ArrowRight, 
  AlertCircle, 
  Building, 
  ArrowLeft, 
  FileCheck, 
  Mail,
  Phone,
  Ruler,
  ClipboardCheck,
  ClipboardList,
  Zap,
  ExternalLink,
  ChevronDown,
  Download,
  Info,
  CheckSquare,
  Layout,
  Layers,
  BookOpen,
  Check,
  MessageSquare,
  Printer,
  ListFilter,
  ImageIcon,
  Search,
  Flag,
  AlertTriangle,
  History,
  Home, 
  Droplets, 
  Wind, 
  Thermometer, 
  Shield, 
  Construction, 
  Wrench, 
  Hammer, 
  Lightbulb, 
  Power, 
  Waves, 
  Flame, 
  Bug, 
  Camera, 
  Box, 
  Scaling,
  AlignLeft
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Inspection, Location, Contact, Agreement, BusinessProfile, InspectionReport, ReportSection, NarrativeTag } from '../types';
import { supabase, toCamelCase, getErrorMessage } from '../lib/supabase';
import SigningPortal from './SigningPortal';

// Portal icon registry
const PORTAL_ICONS: Record<string, any> = {
  'Home': Home,
  'Building': Building,
  'Zap': Zap,
  'Droplets': Droplets,
  'Wind': Wind,
  'Thermometer': Thermometer,
  'Shield': Shield,
  'Construction': Construction,
  'Wrench': Wrench,
  'Hammer': Hammer,
  'Search': Search,
  'Lightbulb': Lightbulb,
  'Power': Power,
  'Waves': Waves,
  'Flame': Flame,
  'Bug': Bug,
  'Camera': Camera,
  'Box': Box,
  'Scaling': Scaling,
  'MapPin': MapPin,
  'Layout': Layout,
  'Layers': Layers,
  'Info': Info,
  'ClipboardList': ClipboardList
};

const PortalIcon = ({ name, size = 16, className = "" }: { name?: string, size?: number, className?: string }) => {
  const IconComponent = name && PORTAL_ICONS[name] ? PORTAL_ICONS[name] : (PORTAL_ICONS['Info']);
  return <IconComponent size={size} className={className} />;
};

interface InspectionPortalProps {
  inspectionId: string;
  businessProfile: BusinessProfile;
  onComplete: () => void;
}

const InspectionPortal: React.FC<InspectionPortalProps> = ({ inspectionId, businessProfile, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [property, setProperty] = useState<Location | null>(null);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [reports, setReports] = useState<InspectionReport[]>([]);
  const [tags, setTags] = useState<NarrativeTag[]>([]);
  const [activeSigningId, setActiveSigningId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'hub' | 'report'>('hub');
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inspectionId) fetchPortalData();
  }, [inspectionId]);

  const fetchPortalData = async () => {
    setLoading(true);
    try {
      const { data: insData } = await supabase.from('inspections').select('*').eq('id', inspectionId).maybeSingle();
      if (!insData) throw new Error("Session expired or invalid.");
      const ins = toCamelCase(insData) as Inspection;
      setInspection(ins);

      const { data: propData } = await supabase.from('locations').select('*').eq('id', ins.propertyId).maybeSingle();
      if (propData) setProperty(toCamelCase(propData) as Location);

      const { data: agrsData } = await supabase.from('agreements').select('*').eq('inspection_id', inspectionId);
      if (agrsData) setAgreements(agrsData.map(toCamelCase));

      const { data: rptData } = await supabase.from('inspection_reports').select('*').eq('inspection_id', inspectionId);
      if (rptData) setReports(rptData.map(toCamelCase));

      const { data: tagData } = await supabase.from('narrative_tags').select('*');
      if (tagData) setTags(tagData.map(toCamelCase));

    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReport = async () => {
    if (!reportRef.current) return;
    setIsGeneratingPDF(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Inspection_Report_${property?.address.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      alert("Print Engine Failure: " + getErrorMessage(err));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getReportStructure = (rpt: InspectionReport) => {
    const answers = rpt.answers || {};
    const raw = answers.structure || answers._structure || rpt.snapshotStructure || (rpt as any).snapshot_structure || [];
    return toCamelCase(raw) as ReportSection[];
  };

  const getReportFindings = (rpt: InspectionReport) => {
    const answers = rpt.answers || {};
    return answers.findings || answers._findings || {};
  };

  // Helper to ensure images are always handled as objects { url, description }
  const hydrateImages = (images: any[]): { url: string, description: string }[] => {
    if (!images) return [];
    return images.map(img => {
      if (typeof img === 'string') return { url: img, description: '' };
      return { url: img.url || '', description: img.description || '' };
    });
  };

  if (loading) return (
    <div className="h-screen bg-[#f8faf7] flex flex-col items-center justify-center space-y-4">
      <Loader2 className="animate-spin text-[#606c38]" size={32} />
      <p className="text-xs font-black uppercase text-slate-400 tracking-widest">Synchronizing Technical Hub...</p>
    </div>
  );

  if (error || !inspection) return (
    <div className="h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
      <AlertCircle size={32} className="text-rose-500 mb-4" />
      <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Access Protocol Invalid</h2>
      <button onClick={() => window.location.href = '/'} className="mt-6 px-6 py-3 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest">Return to Gateway</button>
    </div>
  );

  if (activeSigningId) return <SigningPortal agreementId={activeSigningId} businessProfile={businessProfile} onComplete={() => { setActiveSigningId(null); fetchPortalData(); }} />;

  // Dynamic Aesthetic Configs from Database Settings
  const roundingClass = businessProfile.reportRounding === 'Soft' ? 'rounded-3xl' : 'rounded-xl';
  const brandingColor = businessProfile.reportColor || '#606c38';

  return (
    <div className="min-h-screen bg-[#f8faf7] font-sans pb-12 animate-in fade-in duration-700">
      <div className="relative h-[25vh] overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-t from-[#f8faf7] via-slate-900/60 to-slate-900/20 z-10" />
        {property?.image && <img src={property.image} className="w-full h-full object-cover opacity-40" alt="Backdrop" />}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 text-center">
          <div className="mb-4">
            {businessProfile.logo ? <img src={businessProfile.logo} className="max-h-16 w-auto object-contain" /> : <ShieldCheck size={32} className="text-white" />}
          </div>
          <h1 className="text-white text-4xl font-black tracking-tighter uppercase mb-2">{businessProfile.companyName} PORTAL</h1>
          <p className="text-slate-300 text-xs font-bold uppercase tracking-[0.4em]">{businessProfile.tagline}</p>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto -mt-10 px-4 relative z-30 space-y-10">
        {/* STICKY TAB NAVIGATION - LOCKED TO TOP */}
        <div className="sticky top-0 z-[100] py-4 bg-[#f8faf7]/80 backdrop-blur-md">
          <div className="flex bg-white p-1.5 rounded-2xl border border-slate-200 shadow-2xl w-fit mx-auto">
            <button onClick={() => setActiveTab('hub')} className={`px-12 py-4 rounded-xl text-sm font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === 'hub' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>
                <Layout size={18} /> Asset Hub
            </button>
            <button onClick={() => setActiveTab('report')} className={`px-12 py-4 rounded-xl text-sm font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === 'report' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>
                <FileText size={18} /> Technical Audit
            </button>
          </div>
        </div>

        {activeTab === 'hub' ? (
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl mx-auto">
              <div className="lg:col-span-8 space-y-8">
                 <section className={`bg-white p-12 ${roundingClass} border border-slate-200 shadow-sm animate-in slide-in-from-bottom-4`}>
                    <div className="flex justify-between items-start mb-12">
                       <div>
                         <h2 className="text-4xl font-black text-slate-900 tracking-tight uppercase">Session Registry</h2>
                         <p className="text-slate-500 text-lg font-medium mt-2">Compliance overview and technical schedule.</p>
                       </div>
                       <div className="px-6 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 border border-emerald-100 shadow-sm">
                         <CheckCircle size={16} /> Registry Active
                       </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-slate-50 pt-12">
                       <div className="flex items-start gap-6">
                          <div className="p-5 bg-slate-50 text-slate-900 rounded-2xl shadow-sm"><MapPin size={28} /></div>
                          <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Subject Asset</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight">{property?.address}</p>
                            <p className="text-sm font-bold text-slate-500 mt-2 uppercase tracking-wide">{property?.city}, {property?.state}</p>
                          </div>
                       </div>
                       <div className="flex items-start gap-6">
                          <div className="p-5 bg-slate-50 text-indigo-600 rounded-2xl shadow-sm"><Calendar size={28} /></div>
                          <div>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Audit Schedule</p>
                            <p className="text-2xl font-black text-slate-800 leading-tight">{inspection.date}</p>
                            <p className="text-sm font-bold text-slate-500 mt-2 uppercase tracking-widest">{inspection.time}</p>
                          </div>
                       </div>
                    </div>
                 </section>

                 <div className="grid grid-cols-1 gap-4">
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.3em] px-2">Authorized Protocols</h3>
                    {agreements.map(a => (
                      <div key={a.id} className={`bg-white p-10 ${roundingClass} border border-slate-200 shadow-sm flex items-center justify-between hover:border-slate-300 transition-all group`}>
                         <div className="flex items-center gap-6">
                            <div className={`p-5 rounded-2xl transition-colors ${a.status === 'Signed' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>
                              {a.status === 'Signed' ? <FileCheck size={32} /> : <FileText size={32} />}
                            </div>
                            <div>
                              <h4 className="font-black text-slate-900 text-2xl uppercase tracking-tight leading-none mb-2">{a.title}</h4>
                              <p className="text-sm font-bold text-slate-400 uppercase tracking-[0.2em]">{a.status}</p>
                            </div>
                         </div>
                         {a.status !== 'Signed' ? (
                           <button onClick={() => setActiveSigningId(a.id)} className="px-10 py-4 bg-slate-900 text-white rounded-xl text-sm font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-3 shadow-xl">
                             Sign Protocol <ArrowRight size={18} />
                           </button>
                         ) : (
                           <div className="px-6 py-3 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-black uppercase tracking-widest border border-emerald-100 flex items-center gap-2">
                             <Check size={16} /> Secured
                           </div>
                         )}
                      </div>
                    ))}
                 </div>
              </div>

              <div className="lg:col-span-4 space-y-8">
                 <div className={`bg-white p-10 ${roundingClass} border border-slate-200 shadow-sm`}>
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-10 flex items-center gap-3"><Info size={18} className="text-blue-500" /> Organizational Intel</h4>
                    <div className="flex items-center gap-6 mb-10">
                       <div className="w-16 h-16 flex items-center justify-center shrink-0">
                          {businessProfile.logo ? <img src={businessProfile.logo} className="max-h-16 w-auto object-contain" /> : <Building size={36} className="text-slate-200" />}
                       </div>
                       <div className="min-w-0">
                          <p className="text-xl font-black text-slate-900 uppercase tracking-tighter truncate leading-tight">{businessProfile.companyName}</p>
                          <p className="text-xs font-bold text-slate-400 uppercase truncate mt-1 tracking-widest">{businessProfile.tagline}</p>
                       </div>
                    </div>
                    <div className="space-y-4">
                       <div className="flex items-center gap-5 p-5 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors">
                         <Mail size={20} className="text-slate-400" />
                         <p className="text-base font-bold text-slate-700 truncate">{businessProfile.email}</p>
                       </div>
                       <div className="flex items-center gap-5 p-5 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors">
                         <Phone size={20} className="text-slate-400" />
                         <p className="text-base font-bold text-slate-700">{businessProfile.phone}</p>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        ) : (
           <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {reports.length > 0 ? (
                <>
                  {/* STICKY SIDEBAR NAVIGATION */}
                  <aside className="lg:w-80 shrink-0">
                     <div className="bg-white p-8 border border-slate-200 shadow-2xl sticky top-32 overflow-hidden" style={{ borderRadius: roundingClass === 'rounded-3xl' ? '1.5rem' : '0.75rem' }}>
                        <div className="flex items-center gap-3 mb-10 text-slate-400">
                           <ListFilter size={20} />
                           <span className="text-xs font-black uppercase tracking-[0.3em]">Audit Navigator</span>
                        </div>
                        <nav className="space-y-2 max-h-[50vh] overflow-y-auto no-scrollbar">
                           {reports.map(rpt => {
                              const structure = getReportStructure(rpt);
                              return structure.map(section => (
                                 <a 
                                    key={section.id} 
                                    href={`#sec-${section.id}`} 
                                    className="flex items-center gap-3 p-4 rounded-xl text-sm font-black uppercase text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-all truncate border border-transparent hover:border-slate-100"
                                 >
                                    <PortalIcon name={section.icon} size={14} className="shrink-0" />
                                    <span className="truncate">{section.name}</span>
                                 </a>
                              ));
                           })}
                        </nav>
                        <div className="mt-12 pt-10 border-t border-slate-100">
                           <button 
                             disabled={isGeneratingPDF}
                             onClick={handlePrintReport} 
                             className="w-full py-6 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-[0.3em] flex items-center justify-center gap-4 shadow-2xl hover:bg-slate-800 transition-all active:scale-[0.98]"
                           >
                              {isGeneratingPDF ? <Loader2 size={18} className="animate-spin" /> : <Printer size={18} />} Technical Copy
                           </button>
                        </div>
                     </div>
                  </aside>

                  {/* READABILITY FOCUSED CONTENT AREA */}
                  <div className="flex-1 space-y-16" ref={reportRef}>
                     {reports.map(rpt => {
                        const structure = getReportStructure(rpt);
                        const findingsMap = getReportFindings(rpt);
                        const allFindings = Object.values(findingsMap).flat();
                        
                        const summary = {
                           total: allFindings.length,
                           major: allFindings.filter(f => (f as any).tagIds?.some((tid: string) => tags.find(t => t.id === tid)?.name.toLowerCase().includes('major'))).length,
                           maintenance: allFindings.filter(f => (f as any).tagIds?.some((tid: string) => tags.find(t => t.id === tid)?.name.toLowerCase().includes('main'))).length
                        };

                        return (
                          <div key={rpt.id} className="space-y-20">
                             <div className={`bg-white ${roundingClass} border border-slate-200 shadow-2xl overflow-hidden p-16 md:p-24`}>
                                <div className="flex flex-col md:flex-row justify-between items-start gap-12 border-b-8 border-slate-900 pb-20">
                                   <div className="max-w-2xl">
                                      <h2 className="text-7xl font-black text-slate-900 tracking-tighter uppercase mb-8 leading-[0.85]">Technical Audit Report</h2>
                                      <div className="flex flex-wrap gap-10 text-base font-black text-slate-400 uppercase tracking-[0.3em]">
                                         <p className="flex items-center gap-4"><MapPin size={22} className="text-slate-900" /> {property?.address}</p>
                                         <p className="flex items-center gap-4"><Calendar size={22} className="text-slate-900" /> {inspection.date}</p>
                                      </div>
                                   </div>
                                   <div className="px-12 py-8 bg-emerald-50 text-emerald-600 rounded-2xl border-2 border-emerald-100 flex flex-col items-center justify-center shrink-0 shadow-sm">
                                      <ShieldCheck size={64} className="mb-3" />
                                      <span className="text-sm font-black uppercase tracking-[0.3em]">Verified Snapshot</span>
                                   </div>
                                </div>

                                {businessProfile.showExecutiveSummary !== false && (
                                   <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 pt-20">
                                      <div className="p-12 bg-slate-900 text-white rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden group">
                                         <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform"><BookOpen size={100} /></div>
                                         <p className="text-xs font-black uppercase tracking-[0.4em] text-blue-400 mb-3 relative z-10">Total Findings</p>
                                         <p className="text-7xl font-black tracking-tighter relative z-10">{summary.total}</p>
                                      </div>
                                      <div className={`p-12 rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden group ${summary.major > 0 ? 'bg-rose-50 border-4 border-rose-200 text-rose-600' : 'bg-slate-50 text-slate-400'}`}>
                                         <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform"><AlertTriangle size={100} /></div>
                                         <p className="text-xs font-black uppercase tracking-[0.4em] mb-3 relative z-10">Major Defects</p>
                                         <p className="text-7xl font-black tracking-tighter relative z-10">{summary.major}</p>
                                      </div>
                                      <div className={`p-12 rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden group ${summary.maintenance > 0 ? 'bg-amber-50 border-4 border-amber-200 text-amber-600' : 'bg-slate-50 text-slate-400'}`}>
                                         <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform"><Zap size={100} /></div>
                                         <p className="text-xs font-black uppercase tracking-[0.4em] mb-3 relative z-10">Maintenance</p>
                                         <p className="text-7xl font-black tracking-tighter relative z-10">{summary.maintenance}</p>
                                      </div>
                                   </div>
                                )}
                             </div>

                             <div className="space-y-32">
                                {structure.map(section => (
                                  <section key={section.id} id={`sec-${section.id}`} className="space-y-16 scroll-mt-32">
                                     <div 
                                      className={`flex items-center gap-8 p-12 ${roundingClass} text-white shadow-2xl relative overflow-hidden`}
                                      style={{ backgroundColor: brandingColor }}
                                     >
                                        <div className="absolute top-0 right-0 p-16 opacity-10 -rotate-12"><PortalIcon name={section.icon} size={160} /></div>
                                        <div className="p-6 bg-white/10 rounded-2xl backdrop-blur-md border border-white/20"><PortalIcon name={section.icon} size={40} /></div>
                                        <div>
                                           <h3 className="text-5xl font-black uppercase tracking-tighter">{section.name}</h3>
                                           <p className="text-xs font-bold text-white/60 uppercase tracking-[0.4em] mt-2">Active Technical Flow Protocols</p>
                                        </div>
                                     </div>

                                     <div className="grid grid-cols-1 gap-20 pl-8 md:pl-20 border-l-8 border-slate-200/50">
                                        {section.items.map(item => {
                                          const itemFindings = findingsMap[item.id] || [];
                                          const elementsWithData = item.elements.filter(el => {
                                            const val = rpt.answers[el.id];
                                            return val !== undefined && val !== '' && val !== false && (!Array.isArray(val) || val.length > 0);
                                          });

                                          if (elementsWithData.length === 0 && itemFindings.length === 0) return null;

                                          return (
                                            <div key={item.id} className="space-y-12 animate-in fade-in duration-500">
                                               <h4 className="text-4xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-6">
                                                  <div className="p-3 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: brandingColor }}>
                                                     <PortalIcon name={item.icon} size={20} />
                                                  </div> 
                                                  {item.name}
                                               </h4>
                                               
                                               <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                  {elementsWithData.map(el => (
                                                    <div key={el.id} className={`bg-white p-10 ${roundingClass} border border-slate-200 space-y-4 hover:border-slate-900 transition-all shadow-sm`}>
                                                       <p className="text-sm font-black text-slate-400 uppercase tracking-[0.3em]">{el.label}</p>
                                                       {el.type === 'checkbox' ? (
                                                         <div className="flex items-center gap-4 text-emerald-600 font-black text-lg uppercase tracking-widest">
                                                            {rpt.answers[el.id] ? <CheckCircle size={24} /> : <AlertCircle size={24} className="text-slate-300" />}
                                                            {rpt.answers[el.id] ? 'Verified Functional' : 'Registry Exception'}
                                                         </div>
                                                       ) : Array.isArray(rpt.answers[el.id]) ? (
                                                         <div className="flex flex-wrap gap-3 pt-2">
                                                            {(rpt.answers[el.id] as string[]).map(v => <span key={v} className="px-5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-700 uppercase tracking-tight">{v}</span>)}
                                                         </div>
                                                       ) : (
                                                         <p className="text-2xl font-bold text-slate-900 leading-relaxed tracking-tight uppercase">{rpt.answers[el.id]}</p>
                                                       )}
                                                    </div>
                                                  ))}
                                               </div>

                                               {itemFindings.length > 0 && (
                                                 <div className="space-y-10 mt-8">
                                                    <div className="flex items-center gap-4 text-slate-900 border-b border-slate-100 pb-4">
                                                       <AlertCircle size={28} />
                                                       <span className="text-lg font-black uppercase tracking-[0.4em]">Audit Observations</span>
                                                    </div>
                                                    <div className="grid grid-cols-1 gap-10">
                                                       {itemFindings.map((f: any) => {
                                                         const activeTag = tags.find(t => f.tagIds?.includes(t.id));
                                                         const photos = hydrateImages([...(f.registryImages || []), ...(f.findingImages || [])]);

                                                         return (
                                                           <div key={f.id} className={`p-12 bg-white border-2 ${roundingClass} space-y-10 relative overflow-hidden shadow-2xl hover:shadow-[#606c38]/5 transition-all`} style={{ borderColor: activeTag?.color ? `${activeTag.color}40` : '#e2e8f0' }}>
                                                              <div className="flex flex-col sm:flex-row justify-between items-start gap-8">
                                                                 <div className="flex items-center gap-6">
                                                                    {activeTag && (
                                                                       <div className="px-5 py-2 rounded-full text-xs font-black uppercase tracking-[0.3em] text-white shadow-sm" style={{ backgroundColor: activeTag.color }}>{activeTag.name}</div>
                                                                    )}
                                                                    <h5 className="font-black text-slate-900 text-3xl uppercase tracking-tight leading-none">{f.title}</h5>
                                                                 </div>
                                                              </div>
                                                              
                                                              <div className="prose prose-slate max-w-none text-slate-700 font-medium text-2xl leading-[1.8] pl-8 border-l-8 border-slate-100">
                                                                 <ReactMarkdown remarkPlugins={[remarkGfm]}>{f.content}</ReactMarkdown>
                                                              </div>

                                                              {photos.length > 0 && (
                                                                 <div className="pt-12 border-t border-slate-50">
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-8">
                                                                       {photos.map((img, i) => (
                                                                          <div key={i} className="flex flex-col gap-4">
                                                                            <div className="aspect-video rounded-2xl overflow-hidden border border-slate-100 group/img cursor-pointer shadow-md" onClick={() => window.open(img.url, '_blank')}>
                                                                               <img src={img.url} className="w-full h-full object-cover transition-transform group-hover/img:scale-110 duration-1000" />
                                                                            </div>
                                                                            {img.description && (
                                                                              <div className="flex gap-2 items-start pl-4 border-l-2 border-slate-200">
                                                                                <AlignLeft size={14} className="text-slate-400 mt-0.5 shrink-0" />
                                                                                <p className="text-[11px] font-bold text-slate-500 italic leading-relaxed">{img.description}</p>
                                                                              </div>
                                                                            )}
                                                                          </div>
                                                                       ))}
                                                                    </div>
                                                                 </div>
                                                              )}
                                                           </div>
                                                         );
                                                       })}
                                                    </div>
                                                 </div>
                                               )}
                                            </div>
                                          );
                                        })}
                                     </div>
                                  </section>
                                ))}
                             </div>

                             <div className={`bg-white ${roundingClass} p-24 border border-slate-200 text-center space-y-12 shadow-sm`}>
                                <ShieldCheck size={80} className="mx-auto" style={{ color: brandingColor }} />
                                <div>
                                   <p className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Authorized Audit Finalized</p>
                                   <p className="text-base text-slate-400 font-bold uppercase tracking-[0.5em] mt-4">Registry Secured & Digitally Archived.</p>
                                </div>
                             </div>
                          </div>
                        );
                     })}
                  </div>
                </>
              ) : (
                <div className={`w-full py-80 text-center bg-white ${roundingClass} border border-slate-200 shadow-sm animate-in fade-in`}>
                   <AlertCircle size={100} className="mx-auto text-slate-100 mb-10" />
                   <h3 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Technical Compilation Active</h3>
                </div>
              )}
           </div>
        )}
      </main>

      <footer className="mt-48 py-24 px-8 text-center border-t border-slate-200 bg-white">
         <p className="text-slate-400 text-xs font-black uppercase tracking-[0.8em]">{businessProfile.companyName.toUpperCase()} • TECHNICAL HUB GATEWAY</p>
      </footer>
    </div>
  );
};

export default InspectionPortal;